import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Input, toast } from "@devhop/ui";
import { useMemo, useState } from "react";
import {
  useWorkshopEventsQuery,
  useWorkshopRegistrationsQuery,
  useUpdateWorkshopRegistrationMutation,
} from "../api/queries";
import { WorkshopRegistrationsTable } from "../ui/WorkshopRegistrationsTable";

export function WorkshopRegistrationsPage() {
  const canUpdate = useActionPermission(["workshop-registrations:update"]);
  const [search, setSearch] = useState("");
  const { data: eventsData } = useWorkshopEventsQuery();
  const eventId = eventsData?.items[0]?.id ?? "";

  const query = useMemo(
    () => ({
      limit: 50,
      offset: 0,
      eventId: eventId || undefined,
      search: search.trim() || undefined,
    }),
    [eventId, search],
  );

  const {
    data,
    isPending,
    isError,
    error,
  } = useWorkshopRegistrationsQuery(query);
  const updateMutation = useUpdateWorkshopRegistrationMutation();
  const items = data?.data ?? [];
  const isLoading = isPending;

  const handleStatusChange = async (id: string, status: string) => {
    if (!canUpdate) return;
    try {
      await updateMutation.mutateAsync({ id, body: { status } });
      toast.success("ອັບເດດສະຖານະແລ້ວ");
    } catch {
      toast.error("ອັບເດດບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Dashboard", href: "/app/dashboard" },
            { label: "ໃບສະໝັກ Workshop" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">
              ໃບສະໝັກ Workshop
            </h1>
            <p className="text-muted-foreground text-sm">
              International Workshop · 16–17 Jul 2026 · FREE
            </p>
          </div>
          <Input
            placeholder="ຄົ້ນຫາຊື່ / ເບີໂທ"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
        </div>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : isError ? (
          <p className="text-destructive text-sm" role="alert">
            ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ — {error instanceof Error ? error.message : "ລອງໃໝ່ອີກຄັ້ງ"}
          </p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີໃບສະໝັກ</p>
        ) : (
          <WorkshopRegistrationsTable
            items={items}
            onStatusChange={handleStatusChange}
            updatingId={
              updateMutation.isPending ? updateMutation.variables?.id : undefined
            }
          />
        )}
      </Main>
    </>
  );
}
